import { useEffect, useMemo, useRef } from 'react';
import Chart from 'chart.js/auto';
import { LineChart as LineChartIcon } from 'lucide-react';

/**
 * LineChart component that supports single or multiple datasets
 * 
 * Single dataset mode:
 * <LineChart data={[1,2,3...]} label="Sales" color="#22c55e" title="Chart" />
 * 
 * Multiple datasets mode:
 * <LineChart 
 *   datasets={[
 *     { data: [1,2,3...], label: "Sales", color: "#22c55e" },
 *     { data: [4,5,6...], label: "Views", color: "#3b82f6" }
 *   ]} 
 *   title="Chart" 
 * />
 */
export default function LineChart({ data, labels, color, datasets, title }) {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Quem chama costuma passar `datasets={[...]}` inline (nova referência a cada
    // render do pai). Depender da referência destruía e recriava o gráfico — com a
    // animação de entrada — sempre que outra seção da página carregava ("piscava").
    // Depende do conteúdo: só redesenha quando os dados mudam de fato.
    const dataKey = useMemo(
        () => JSON.stringify({ data, labels, color, datasets }),
        [data, labels, color, datasets]
    );

    useEffect(() => {
        if (!chartRef.current) return;

        // Determine se está usando single ou multiple datasets
        let chartDatasets;

        if (datasets && Array.isArray(datasets)) {
            // Multiple datasets mode
            chartDatasets = datasets.map(ds => ({
                label: ds.label,
                data: ds.data,
                borderColor: ds.color,
                backgroundColor: ds.color + '20',
                tension: 0.4,
                fill: true,
                pointRadius: 3,
                pointHoverRadius: 6,

                pointStyle: 'circle',
                pointBackgroundColor: ds.color,   // 👈 FUNDO DA BOLA
                pointBorderColor: ds.color,       // 👈 BORDA DA BOLA
                pointBorderWidth: 0,              // 👈 REMOVE BORDA


                borderWidth: 2
            }));
        } else if (data && Array.isArray(data)) {
            // Single dataset mode (backward compatibility)
            chartDatasets = [{
                label: labels,
                data: data,
                borderColor: color,
                backgroundColor: color + '20',
                tension: 0.4,
                fill: true,
                pointRadius: 3,
                pointHoverRadius: 5,
                borderWidth: 2
            }];
        } else {
            return; // Sem dados válidos
        }

        // Destruir gráfico anterior
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const ctx = chartRef.current.getContext('2d');

        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: labels || Array.from({ length: 24 }, (_, i) => `${i}h`),
                datasets: chartDatasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                plugins: {
                    legend: {
                        display: chartDatasets.length > 1,
                        position: 'top',
                        labels: {
                            color: '#e5e7eb',
                            padding: 15,
                            font: { size: 12 },
                            usePointStyle: true
                        }
                    },
                    tooltip: {
                        backgroundColor: '#0d1014',
                        titleColor: '#e5e7eb',
                        bodyColor: '#e5e7eb',
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                        borderWidth: 1,
                        padding: 12,
                        displayColors: true,
                        usePointStyle: true,
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            color: '#94a3b8',
                            font: { size: 11 }
                        },
                        precision: 0,
                        stepSize: 1,

                        grid: {
                            display: false,
                            drawBorder: false,
                        }
                    },
                    x: {
                        ticks: {
                            color: '#94a3b8',
                            font: { size: 10 }
                        },
                        grid: {
                            display: false,
                            drawBorder: false,
                        }
                    }
                }
            }
        });

        // Cleanup
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataKey]);

    return (
        <div className="statistics-chart-card">
            <h3><LineChartIcon size={16} strokeWidth={2.5} /> {title}</h3>
            <div className="statistics-chart-container">
                <canvas ref={chartRef}></canvas>
            </div>
        </div>
    );
}
